// src/components/my-journey/SquadLifecycle.jsx
// Feature — Squad Lifecycle (Alluvial / Flow Diagram)
// Visualises how a manager's squad evolved across three season eras using
// an SVG flow diagram with cubic bezier connecting paths.
//
// Era definitions:
//   Era 1 — GW 1–12   (early season)
//   Era 2 — GW 13–26  (mid season)
//   Era 3 — GW 27–38  (run-in)

import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLeagueData, useManagerContext } from '../../context/DataContext';

// ── Design tokens ──────────────────────────────────────────────────────
const TOKEN = {
  background: '#000006',
  blue: '#3f69ae',
  turquoise: '#44d5a3',
  red: '#eb5757',
  surface1: '#1d1d1d',
  surface2: '#272727',
  border: '#4a4a4a',
  grey: '#8a8a8a',
};

// ── Player classification colours ──────────────────────────────────────────────
// Loyalists (all 3 eras)   — turquoise
// Arrivals (Era 2 or 3)    — violet/purple
// Sold (only Era 1)        — red (they left)
// Two-era players          — cyan
// Toxic Lovers             — orange
// Bench players            — muted grey
const COLOUR_LOYALIST = '#44d5a3';
const COLOUR_ARRIVAL  = '#9b7fe8';
const COLOUR_SOLD     = '#eb5757';
const COLOUR_TWO_ERA  = '#44b6c5';
const COLOUR_TOXIC    = '#f5a623'; // orange — on-off toxic relationship
const COLOUR_BENCH    = 'rgba(138,138,138,0.5)';

// ── SVG canvas constants ────────────────────────────────────────────────────────
const COLUMN_POSITIONS = [120, 380, 640]; // x centres of the three era columns
const COLUMN_WIDTH     = 20;
const CANVAS_HEIGHT    = 500;
const PADDING_TOP      = 40;
const USABLE_HEIGHT    = CANVAS_HEIGHT - PADDING_TOP * 2;
const MAX_PLAYERS      = 12;
const NODE_GAP         = 4;  // px gap between stacked nodes
const MIN_NODE_HEIGHT  = 4;  // px minimum node height
const MAX_NODE_HEIGHT  = 60; // px maximum node height
const BEZIER_OFFSET    = 80; // horizontal control-point offset for the cubic bezier

// ── Era label map ───────────────────────────────────────────────────────────────
const ERA_LABELS = {
  1: 'GW 1–12',
  2: 'GW 13–26',
  3: 'GW 27–38',
};

// ── Truncate player name for node label ─────────────────────────────────────────
const truncateName = (name, maxChars = 12) =>
  name && name.length > maxChars ? `${name.slice(0, maxChars)}…` : name;

// ── Classify a player based on which eras they appeared in ──────────────────────
// NOTE: Transfer_In_Count is not available in the Squad Timeline data — the scraper
// only records Transfer_In_GW (first GW present) and Transfer_Out_GW (last GW present).
// Toxic Lover falls back to the Era 1 + Era 3 presence pattern as a proxy.
// TODO: Add Transfer_In_Count to extract_squad_timeline() in data.py by counting
//       the number of distinct transfer windows a player was brought back in,
//       then use Transfer_In_Count >= 3 as the Toxic Lover threshold here.
const classifyPlayer = (erasPresent, totalBench, totalStarts, totalPoints) => {
  // Bench Warmer — benched more than started but low points (not an Impact Sub)
  if (totalBench > totalStarts) {
    if (totalPoints > 20) {
      return { colour: COLOUR_BENCH, label: 'Impact Sub' };
    }
    return { colour: COLOUR_BENCH, label: 'Bench Warmer' };
  }

  const sorted = [...erasPresent].sort();
  if (sorted.length === 3) return { colour: COLOUR_LOYALIST, label: 'Loyalist' };
  if (sorted.length === 1) {
    if (sorted[0] === 1) return { colour: COLOUR_SOLD, label: 'Early Exit' };
    return { colour: COLOUR_ARRIVAL, label: 'Late Arrival' };
  }
  // Exactly two eras
  if (!sorted.includes(1)) return { colour: COLOUR_ARRIVAL, label: 'Late Arrival' };
  // Era 1 + Era 3 but not Era 2 = Toxic Lover (sold and rebought — came back Era 1 and 3;
  // transfer history not available for precise count)
  if (sorted[0] === 1 && sorted[1] === 3) return { colour: COLOUR_TOXIC, label: 'Toxic Lover' };
  return { colour: COLOUR_TWO_ERA, label: 'Kickstarter' };
};

// ── Compute node layout for a single era column ─────────────────────────────────
// Returns an array of { player, eraPoints, nodeHeight, cy, colour, erasPresent, benchOnly, totalPoints }
const computeColumnLayout = (eraNum, eraRows, playerMeta) => {
  // Sort by era points descending, take top MAX_PLAYERS
  const sorted = [...eraRows]
    .sort((a, b) => b.Era_Points - a.Era_Points)
    .slice(0, MAX_PLAYERS);

  if (sorted.length === 0) return [];

  const totalEraPoints = sorted.reduce((sum, r) => sum + r.Era_Points, 0);

  // Compute proportional heights clamped to [MIN_NODE_HEIGHT, MAX_NODE_HEIGHT]
  const rawHeights = sorted.map((r) =>
    totalEraPoints > 0
      ? Math.min(MAX_NODE_HEIGHT, Math.max(MIN_NODE_HEIGHT, (r.Era_Points / totalEraPoints) * USABLE_HEIGHT))
      : MIN_NODE_HEIGHT
  );

  // Total stack height including gaps
  const stackHeight = rawHeights.reduce((s, h) => s + h, 0) + (sorted.length - 1) * NODE_GAP;
  // Start y so the stack is vertically centred in USABLE_HEIGHT
  const startY = PADDING_TOP + (USABLE_HEIGHT - stackHeight) / 2;

  let currentY = startY;
  return sorted.map((row, idx) => {
    const nodeHeight = rawHeights[idx];
    const cy = currentY + nodeHeight / 2; // vertical centre of the node
    currentY += nodeHeight + NODE_GAP;

    const meta = playerMeta[row.Player_Name] || {};
    return {
      player: row.Player_Name,
      eraPoints: row.Era_Points,
      nodeHeight,
      cy,
      colour: meta.colour || COLOUR_BENCH,
      erasPresent: meta.erasPresent || new Set([eraNum]),
      benchOnly: meta.benchOnly || false,
      totalPoints: meta.totalPoints || row.Era_Points,
    };
  });
};

// ── Derive player metadata from all eras ────────────────────────────────────────
const derivePlayerMeta = (managerRows) => {
  const players = {};

  managerRows.forEach((row) => {
    const name = row.Player_Name;
    if (!players[name]) {
      players[name] = {
        totalPoints: 0,
        erasPresent: new Set(),
        totalStarts: 0,
        totalBench: 0,
        eraData: {},
      };
    }
    players[name].totalPoints += row.Era_Points || 0;
    players[name].erasPresent.add(row.Era);
    players[name].totalStarts += row.Starts || 0;
    players[name].totalBench += row.Bench_Count || 0;
    players[name].eraData[row.Era] = row;
  });

  // Classify and annotate each player
  const meta = {};
  Object.entries(players).forEach(([name, info]) => {
    const classification = classifyPlayer(
      info.erasPresent,
      info.totalBench,
      info.totalStarts,
      info.totalPoints,
    );
    meta[name] = {
      ...info,
      // benchOnly kept for backward-compat: true when bench > starts AND low points
      benchOnly: info.totalBench > info.totalStarts && info.totalPoints <= 20,
      colour: classification.colour,
      classLabel: classification.label,
    };
  });
  return meta;
};

// ── SVG Flow Path between two nodes ────────────────────────────────────────────
// Uses a cubic bezier: M sourceX,sourceCy C (sourceX+80),sourceCy (targetX-80),targetCy targetX,targetCy
const FlowPath = ({ sourceX, sourceCy, sourceHeight, targetX, targetCy, targetHeight, colour, isActive, hasHovered }) => {
  const strokeWidth = Math.min(sourceHeight, targetHeight) * 0.8;
  // Change 2: thin connectors — clamp to [1, 4] instead of [2, 20]
  const clampedWidth = Math.max(1, Math.min(4, strokeWidth));

  // Dim all paths when one is hovered, unless this is the active one
  const opacity = hasHovered ? (isActive ? 1 : 0.15) : 0.75;

  const d = `M ${sourceX},${sourceCy} C ${sourceX + BEZIER_OFFSET},${sourceCy} ${targetX - BEZIER_OFFSET},${targetCy} ${targetX},${targetCy}`;

  return (
    <motion.path
      d={d}
      fill="none"
      stroke={colour}
      strokeWidth={clampedWidth}
      strokeLinecap="round"
      // Draw-in animation using pathLength
      initial={{ pathLength: 0, opacity: 0 }}
      animate={{ pathLength: 1, opacity }}
      transition={{
        pathLength: { duration: 0.8, ease: 'easeInOut' },
        opacity: { duration: 0.3 },
      }}
      style={{ cursor: 'pointer' }}
    />
  );
};

// ── Tooltip shown on path / node hover ─────────────────────────────────────────
const Tooltip = ({ content, x, y }) => {
  if (!content) return null;
  return (
    <AnimatePresence>
      <motion.div
        key="tooltip"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 8 }}
        transition={{ duration: 0.18 }}
        style={{
          position: 'absolute',
          // Position tooltip relative to the SVG container
          left: Math.min(x, 700),
          top: Math.max(y - 60, 0),
          backgroundColor: '#1d1d1d',
          border: '1px solid #4a4a4a',
          borderRadius: '8px',
          padding: '8px 12px',
          pointerEvents: 'none',
          zIndex: 100,
          minWidth: '140px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
        }}
      >
        {content.map((line, i) => (
          <div
            key={i}
            style={{
              fontSize: i === 0 ? '12px' : '11px',
              fontWeight: i === 0 ? 700 : 400,
              color: i === 0 ? '#ffffff' : '#8a8a8a',
              lineHeight: 1.4,
            }}
          >
            {line}
          </div>
        ))}
      </motion.div>
    </AnimatePresence>
  );
};

// ── Legend dot ──────────────────────────────────────────────────────────────────
const LegendDot = ({ colour, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
    <div
      style={{
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        backgroundColor: colour,
        flexShrink: 0,
      }}
    />
    <span style={{ fontSize: '11px', color: TOKEN.grey }}>{label}</span>
  </div>
);

// ── Main component ──────────────────────────────────────────────────────────────
const SquadLifecycle = () => {
  const { data } = useLeagueData();
  const { selectedManager } = useManagerContext();

  // Tooltip and hover state
  const [hoveredPlayer, setHoveredPlayer] = useState(null);
  const [tooltipContent, setTooltipContent] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  // ── Early-exit: empty state ───────────────────────────────────────────────────
  const squadTimeline = data?.squadTimeline;
  if (!squadTimeline || squadTimeline.length === 0) {
    return (
      // Change 6: remove height/overflowY from outer div — parent controls scrolling
      <div
        style={{
          backgroundColor: TOKEN.background,
          padding: '24px 16px',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{ textAlign: 'center', maxWidth: '400px' }}>
          <div style={{ fontSize: '32px', marginBottom: '16px' }}>📋</div>
          <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#ffffff', marginBottom: '8px' }}>
            Squad story coming soon
          </h3>
          <p style={{ fontSize: '13px', color: TOKEN.grey, lineHeight: 1.6 }}>
            This feature requires an updated scraper run — the Squad Timeline sheet was not found in the loaded data.
          </p>
        </div>
      </div>
    );
  }

  if (!selectedManager) return null;

  // ── Filter to current manager ─────────────────────────────────────────────────
  const managerRows = squadTimeline.filter((r) => r.Manager === selectedManager);

  // ── Derive per-player metadata (classification, totals, eras) ────────────────
  // Memoised inline — hook ordering is stable because this component only
  // renders when selectedManager is truthy.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const playerMeta = useMemo(() => derivePlayerMeta(managerRows), [managerRows]);

  // ── Filter out zero-total players ────────────────────────────────────────────
  const validPlayers = new Set(
    Object.entries(playerMeta)
      .filter(([, meta]) => meta.totalPoints > 0)
      .map(([name]) => name)
  );

  // ── Pick global top 12 by total points to limit diagram complexity ───────────
  const top12Names = new Set(
    Object.entries(playerMeta)
      .filter(([name]) => validPlayers.has(name))
      .sort((a, b) => b[1].totalPoints - a[1].totalPoints)
      .slice(0, MAX_PLAYERS)
      .map(([name]) => name)
  );

  // ── Change 3: identify top 3 golden players for inline SVG ⭐ markers ─────────
  const goldenNames = new Set(
    Object.entries(playerMeta)
      .filter(([name]) => top12Names.has(name) && !playerMeta[name].benchOnly)
      .sort((a, b) => b[1].totalPoints - a[1].totalPoints)
      .slice(0, 3)
      .map(([name]) => name)
  );

  // ── Build per-era rows, restricted to top 12 ─────────────────────────────────
  const eraRows = { 1: [], 2: [], 3: [] };
  managerRows.forEach((row) => {
    if (top12Names.has(row.Player_Name) && row.Era_Points > 0) {
      eraRows[row.Era].push(row);
    }
  });

  // ── Compute layout for each column ───────────────────────────────────────────
  const columns = {
    1: computeColumnLayout(1, eraRows[1], playerMeta),
    2: computeColumnLayout(2, eraRows[2], playerMeta),
    3: computeColumnLayout(3, eraRows[3], playerMeta),
  };

  // ── Build a lookup: player -> layout node for each era ───────────────────────
  const nodeByEra = {};
  [1, 2, 3].forEach((era) => {
    nodeByEra[era] = {};
    columns[era].forEach((node) => {
      nodeByEra[era][node.player] = node;
    });
  });

  // ── Build flow paths between adjacent era columns ────────────────────────────
  // A path is drawn for a player appearing in both adjacent eras.
  const buildFlowPaths = (fromEra, toEra, fromColX, toColX) => {
    const paths = [];
    const fromNodes = columns[fromEra];

    fromNodes.forEach((fromNode, idx) => {
      const toNode = nodeByEra[toEra][fromNode.player];
      if (!toNode) return; // Player not present in the next era

      const sourceX = fromColX + COLUMN_WIDTH / 2; // right edge of source node
      const targetX = toColX - COLUMN_WIDTH / 2;   // left edge of target node

      paths.push({
        id: `${fromNode.player}-era${fromEra}-era${toEra}`,
        player: fromNode.player,
        sourceX,
        sourceCy: fromNode.cy,
        sourceHeight: fromNode.nodeHeight,
        targetX,
        targetCy: toNode.cy,
        targetHeight: toNode.nodeHeight,
        colour: fromNode.colour,
        delay: idx * 0.06, // stagger by 60ms per path (spec)
      });
    });
    return paths;
  };

  const paths12 = buildFlowPaths(1, 2, COLUMN_POSITIONS[0], COLUMN_POSITIONS[1]);
  const paths23 = buildFlowPaths(2, 3, COLUMN_POSITIONS[1], COLUMN_POSITIONS[2]);

  // ── SVG canvas width derived from rightmost column ────────────────────────────
  const SVG_WIDTH = COLUMN_POSITIONS[2] + COLUMN_WIDTH / 2 + 100; // right margin for labels

  // ── Hover handlers ────────────────────────────────────────────────────────────
  const handleMouseEnterPath = (path, event) => {
    const midX = (path.sourceX + path.targetX) / 2;
    const midY = (path.sourceCy + path.targetCy) / 2;
    const meta = playerMeta[path.player];
    setHoveredPlayer(path.player);
    setTooltipContent([
      path.player,
      `Eras: ${[...meta.erasPresent].sort().map((e) => ERA_LABELS[e]).join(', ')}`,
      `Total: ${meta.totalPoints} pts`,
      meta.classLabel,
    ]);
    setTooltipPos({ x: midX, y: midY });
  };

  const handleMouseEnterNode = (node, event) => {
    setHoveredPlayer(node.player);
    setTooltipContent([
      node.player,
      `Era points: ${node.eraPoints} pts`,
      `Total: ${node.totalPoints} pts`,
      playerMeta[node.player]?.classLabel || '',
    ]);
    setTooltipPos({ x: COLUMN_POSITIONS[0], y: node.cy });
  };

  const handleMouseLeave = () => {
    setHoveredPlayer(null);
    setTooltipContent(null);
  };

  const hasHovered = hoveredPlayer !== null;

  return (
    // Change 6: outer div no longer owns height or overflowY — parent controls scrolling
    <div
      style={{
        backgroundColor: TOKEN.background,
        padding: '24px 16px',
        boxSizing: 'border-box',
      }}
    >
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: '20px', maxWidth: '780px', margin: '0 auto 20px' }}>
        <h2
          style={{
            fontSize: '22px',
            fontWeight: 800,
            color: '#ffffff',
            marginBottom: '4px',
            letterSpacing: '-0.02em',
          }}
        >
          Your Squad Story
        </h2>
        <p style={{ fontSize: '13px', color: TOKEN.grey, marginBottom: '16px' }}>
          Three chapters of your season
        </p>

        {/* Change 5: legend uses "Impact Sub" instead of "Bench only" */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
          <LegendDot colour={COLOUR_LOYALIST} label="Loyalists (all 3 eras)" />
          <LegendDot colour={COLOUR_ARRIVAL}  label="Late Arrivals" />
          <LegendDot colour={COLOUR_SOLD}     label="Early Exits" />
          <LegendDot colour={COLOUR_TOXIC}    label="Toxic Lovers" />
          <LegendDot colour={COLOUR_TWO_ERA}  label="Kickstarter" />
          <LegendDot colour={COLOUR_BENCH}    label="Impact Sub" />
        </div>
      </div>

      {/* ── Diagram card ───────────────────────────────────────────────────── */}
      <div
        style={{
          maxWidth: '780px',
          margin: '0 auto',
          backgroundColor: TOKEN.surface1,
          border: `1px solid ${TOKEN.border}`,
          borderRadius: '14px',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Era column header labels */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-around',
            padding: '14px 20px 0',
            backgroundColor: TOKEN.surface2,
            borderBottom: `1px solid ${TOKEN.border}`,
          }}
        >
          {[1, 2, 3].map((era) => (
            <div
              key={era}
              style={{
                textAlign: 'center',
                flex: 1,
              }}
            >
              <div
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  color: TOKEN.turquoise,
                  textTransform: 'uppercase',
                  marginBottom: '2px',
                }}
              >
                Era {era}
              </div>
              <div
                style={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#ffffff',
                  paddingBottom: '12px',
                }}
              >
                {ERA_LABELS[era]}
              </div>
            </div>
          ))}
        </div>

        {/* ── Explainer ──────────────────────────────────────────────────── */}
        <div
          style={{
            padding: '10px 20px',
            backgroundColor: 'rgba(63,105,174,0.08)',
            borderBottom: `1px solid ${TOKEN.border}`,
            display: 'flex',
            flexWrap: 'wrap',
            gap: '16px',
          }}
        >
          {[
            { icon: '↕', text: 'Height = points scored in that era' },
            { icon: '⬆', text: 'Position = ranked by era contribution' },
            { icon: '〜', text: 'Lines connect the same player across eras' },
          ].map(({ icon, text }) => (
            <div key={text} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '12px', color: TOKEN.turquoise }}>{icon}</span>
              <span style={{ fontSize: '11px', color: TOKEN.grey }}>{text}</span>
            </div>
          ))}
        </div>

        {/* SVG diagram + tooltip wrapper */}
        <div style={{ position: 'relative', overflowX: 'auto' }}>
          {/* Tooltip overlay — positioned absolutely relative to this div */}
          {tooltipContent && (
            <Tooltip content={tooltipContent} x={tooltipPos.x} y={tooltipPos.y} />
          )}

          <svg
            width={SVG_WIDTH}
            height={CANVAS_HEIGHT}
            style={{ display: 'block', minWidth: '100%' }}
            onMouseLeave={handleMouseLeave}
          >
            {/* ── Era column background bands ──────────────────────────── */}
            {COLUMN_POSITIONS.map((cx, i) => (
              <rect
                key={i}
                x={cx - 40}
                y={0}
                width={80}
                height={CANVAS_HEIGHT}
                fill={i % 2 === 0 ? 'rgba(255,255,255,0.015)' : 'rgba(255,255,255,0.025)'}
              />
            ))}

            {/* ── Flow paths Era 1 → Era 2 ─────────────────────────────── */}
            {paths12.map((path) => (
              <g
                key={path.id}
                onMouseEnter={(e) => handleMouseEnterPath(path, e)}
                onMouseLeave={handleMouseLeave}
              >
                <FlowPath
                  sourceX={path.sourceX}
                  sourceCy={path.sourceCy}
                  sourceHeight={path.sourceHeight}
                  targetX={path.targetX}
                  targetCy={path.targetCy}
                  targetHeight={path.targetHeight}
                  colour={path.colour}
                  isActive={hoveredPlayer === path.player}
                  hasHovered={hasHovered}
                />
              </g>
            ))}

            {/* ── Flow paths Era 2 → Era 3 ─────────────────────────────── */}
            {paths23.map((path) => (
              <g
                key={path.id}
                onMouseEnter={(e) => handleMouseEnterPath(path, e)}
                onMouseLeave={handleMouseLeave}
              >
                <FlowPath
                  sourceX={path.sourceX}
                  sourceCy={path.sourceCy}
                  sourceHeight={path.sourceHeight}
                  targetX={path.targetX}
                  targetCy={path.targetCy}
                  targetHeight={path.targetHeight}
                  colour={path.colour}
                  isActive={hoveredPlayer === path.player}
                  hasHovered={hasHovered}
                />
              </g>
            ))}

            {/* ── Player nodes, labels, and ⭐ golden markers for each era ── */}
            {[1, 2, 3].map((era) => {
              const colX = COLUMN_POSITIONS[era - 1];
              return (
                <g key={`era-${era}`}>
                  {columns[era].map((node, idx) => {
                    const isNodeActive = hoveredPlayer === node.player;
                    const nodeOpacity = hasHovered ? (isNodeActive ? 1 : 0.25) : 0.85;

                    return (
                      <g
                        key={node.player}
                        onMouseEnter={(e) => handleMouseEnterNode(node, e)}
                        onMouseLeave={handleMouseLeave}
                        style={{ cursor: 'pointer' }}
                      >
                        {/* Node rectangle */}
                        <motion.rect
                          x={colX - COLUMN_WIDTH / 2}
                          y={node.cy - node.nodeHeight / 2}
                          width={COLUMN_WIDTH}
                          height={node.nodeHeight}
                          rx={3}
                          fill={node.colour}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: nodeOpacity }}
                          transition={{
                            opacity: { duration: 0.3 },
                            default: { duration: 0.4, delay: idx * 0.03 },
                          }}
                        />

                        {/* ⭐ star is now rendered inline with the player name labels — no separate marker needed */}

                        {/* Left column labels — right-aligned at left edge, show if eraPoints > 10 */}
                        {era === 1 && node.eraPoints > 10 && (
                          <motion.text
                            x={colX - COLUMN_WIDTH / 2 - 6}
                            y={node.cy + 4}
                            textAnchor="end"
                            fill={TOKEN.grey}
                            fontSize={11}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: isNodeActive ? 1 : hasHovered ? 0.3 : 0.8 }}
                            transition={{ duration: 0.3, delay: idx * 0.03 + 0.3 }}
                            style={{ fontFamily: 'Roboto, sans-serif', pointerEvents: 'none' }}
                          >
                            {truncateName(node.player)}{goldenNames.has(node.player) ? ' ⭐' : ''}
                          </motion.text>
                        )}

                        {/* Right column labels — left-aligned at right edge */}
                        {era === 3 && node.eraPoints > 10 && (
                          <motion.text
                            x={colX + COLUMN_WIDTH / 2 + 6}
                            y={node.cy + 4}
                            textAnchor="start"
                            fill={TOKEN.grey}
                            fontSize={11}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: isNodeActive ? 1 : hasHovered ? 0.3 : 0.8 }}
                            transition={{ duration: 0.3, delay: idx * 0.03 + 0.3 }}
                            style={{ fontFamily: 'Roboto, sans-serif', pointerEvents: 'none' }}
                          >
                            {goldenNames.has(node.player) ? '⭐ ' : ''}{truncateName(node.player)}
                          </motion.text>
                        )}
                      </g>
                    );
                  })}
                </g>
              );
            })}
          </svg>
        </div>

        {/* ── Player count summary footer ─────────────────────────────────── */}
        <div
          style={{
            padding: '12px 20px',
            borderTop: `1px solid ${TOKEN.border}`,
            backgroundColor: TOKEN.surface2,
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '8px',
          }}
        >
          {[1, 2, 3].map((era) => (
            <div key={era} style={{ textAlign: 'center', flex: 1 }}>
              <div style={{ fontSize: '18px', fontWeight: 800, color: '#ffffff' }}>
                {columns[era].length}
              </div>
              <div style={{ fontSize: '11px', color: TOKEN.grey }}>
                {ERA_LABELS[era]} players
              </div>
            </div>
          ))}
          <div style={{ textAlign: 'center', flex: 1 }}>
            <div style={{ fontSize: '18px', fontWeight: 800, color: TOKEN.turquoise }}>
              {Object.values(playerMeta).filter((m) => m.erasPresent.size === 3 && !m.benchOnly).length}
            </div>
            <div style={{ fontSize: '11px', color: TOKEN.grey }}>Loyalists</div>
          </div>
        </div>
      </div>

      {/* ── Player Type Breakdown ─────────────────────────────────────────── */}
      {/* Change 3: "Squad Breakdown" section label removed — cards only */}
      <div style={{ maxWidth: '780px', margin: '20px auto 0' }}>
        {/* Player type cards */}
        {(() => {
          // Change 5: Impact Sub replaces Bench only; Bench Warmer is omitted
          const typeGroups = {
            'Loyalist':     { colour: COLOUR_LOYALIST, label: 'Loyalists',    desc: 'Your most trusted players — present all season', players: [] },
            'Toxic Lover':  { colour: COLOUR_TOXIC,    label: 'Toxic Lovers', desc: 'Sold mid-season but rebought later', players: [] },
            'Late Arrival': { colour: COLOUR_ARRIVAL,  label: 'Late Arrivals', desc: 'Joined the squad in Era 2 or 3', players: [] },
            'Early Exit':   { colour: COLOUR_SOLD,     label: 'Early Exits',  desc: 'Only appeared in Era 1, then were moved on', players: [] },
            'Impact Sub':   { colour: COLOUR_BENCH,    label: 'Impact Subs',  desc: 'Came off the bench more than they started, but still delivered', players: [] },
          };

          Object.entries(playerMeta).forEach(([name, meta]) => {
            if (!top12Names.has(name)) return;
            // Change 5: skip Bench Warmer (low-point bench players)
            if (meta.classLabel === 'Bench Warmer') return;
            if (typeGroups[meta.classLabel]) {
              typeGroups[meta.classLabel].players.push({ name, totalPoints: meta.totalPoints });
            }
          });

          return (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '10px', marginBottom: '16px' }}>
              {Object.entries(typeGroups)
                .filter(([, g]) => g.players.length > 0)
                .map(([key, group]) => (
                  <div
                    key={key}
                    style={{
                      backgroundColor: TOKEN.surface1,
                      border: `1px solid ${TOKEN.border}`,
                      borderLeft: `3px solid ${group.colour}`,
                      borderRadius: '10px',
                      padding: '12px 14px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: 'white' }}>{group.label}</span>
                      <span style={{
                        fontSize: '11px', fontWeight: 700, color: group.colour,
                        backgroundColor: `${group.colour}18`, borderRadius: '8px', padding: '1px 7px',
                      }}>
                        {group.players.length}
                      </span>
                    </div>
                    <p style={{ fontSize: '10px', color: TOKEN.grey, marginBottom: '8px', lineHeight: 1.4 }}>{group.desc}</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                      {group.players
                        .sort((a, b) => b.totalPoints - a.totalPoints)
                        .slice(0, 3)
                        .map((p) => (
                          <div key={p.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{p.name}</span>
                            <span style={{ fontSize: '10px', color: TOKEN.grey, flexShrink: 0, marginLeft: '6px' }}>{p.totalPoints}pts</span>
                          </div>
                        ))}
                      {group.players.length > 3 && (
                        <span style={{ fontSize: '10px', color: TOKEN.grey }}>+{group.players.length - 3} more</span>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          );
        })()}
      </div>

      {/* ── Bottom spacing for scroll ──────────────────────────────────────── */}
      <div style={{ height: '40px' }} />
    </div>
  );
};

export default SquadLifecycle;
